import type { ECountry, ECountryCode } from "@/domains/country/country.types.ts";
import type { TAutoDiagModulatorDto } from "@/types/backoffice/dto/autodiag/auto-diag-modulator-dto";
import type { TModulatorDto } from "@/types/backoffice/dto/modulator/modulator-dto";
import type { ClassValue } from "clsx";

import { clsx } from "clsx";
import moment from "moment";
import { twMerge } from "tailwind-merge";

import { findCountryDefinitionByCountry } from "@/countries/country-definition.ts";

export type IsFunction<T, R, Fallback = T> = T extends (...args: any[]) => any ? R : Fallback;
export type IsObject<T, R, Fallback = T> = IsFunction<T, Fallback, T extends object ? R : Fallback>;
export type Tail<S> = S extends `${string}.${infer T}` ? Tail<T> : S;
export type Value<T> = T[keyof T];

export type Flatten<ObjectType> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}` | `${Key}.${Flatten<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

export type FlattenType<T, K extends Flatten<T> | string> = K extends `${infer A}.${infer B}`
  ? A extends keyof T
    ? FlattenType<T[A], B>
    : never
  : K extends keyof T
    ? T[K]
    : never;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function fromStringToDate(str: string) {
  return fromStringToMoment(str).toDate();
}

export function fromStringToMoment(str: string) {
  return moment(str);
}

// Returns 'true' if an element was found and scrolled to
export function scrollToFirstError(selector: string = ".p-invalid"): boolean {
  const el = document.querySelector(selector);
  if (el) {
    el.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center",
    });
  }

  return !!el;
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(0))} ${sizes[i]}`;
}

export function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;

  const matrix: number[][] = [];
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [];
    for (let j = 0; j <= len2; j++) {
      if (i === 0) {
        matrix[i][j] = j;
      } else if (j === 0) {
        matrix[i][j] = i;
      } else {
        matrix[i][j] = 0;
      }
    }
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }

  return matrix[len1][len2];
}

export function assignDefined<T>(target: T, ...sources: any[]) {
  for (const source of sources) {
    for (const key of Object.keys(source)) {
      const val = (source as never)[key];
      if (val !== undefined) {
        target[key as keyof T] = val;
      }
    }
  }
  return target;
}

export function assignDeepDefined<T>(target: T, ...sources: any[]) {
  for (const source of sources) {
    for (const key of Object.keys(source)) {
      const val = (source as never)[key];
      if (val !== undefined) {
        if (typeof key === "object") {
          target[key as keyof T] = assignDeepDefined(target[key], val);
        } else {
          target[key as keyof T] = val;
        }
      }
    }
  }
  return target;
}

export function normalizeString(input?: string | null): string {
  if (!input) {
    return "";
  }

  const stripped = input.trim();
  const withoutAccents = stripped.normalize("NFD").replace(/[\u0300-\u036F]/g, "");
  const lowercase = withoutAccents.toLowerCase();
  const withoutSpaces = lowercase.replace(/\s+/g, "");
  return withoutSpaces;
}

export function normalizedStringsEquals(str1?: string | null, str2?: string | null) {
  if (!str1 && !str2) {
    return true;
  }

  if (!str1 && str2) {
    return false;
  }

  if (str2 && !str1) {
    return false;
  }

  return normalizeString(str1) === normalizeString(str2);
}

export function capitalizeWords(inputString: string): string {
  try {
    return inputString
      .trim()
      .split(/\s+/)
      .map((w) => `${w[0]?.toUpperCase()}${w.slice(1).toLowerCase()}`.trim())
      .join(" ");
  } catch (e) {
    console.error(e);
    return inputString;
  }
}

export function stringToColour(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let colour = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0x9a;
    colour += `00${value.toString(16)}`.substr(-2);
  }

  return colour;
}

export async function sleep(ms: number) {
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function modulatorName(mod?: TModulatorDto | TAutoDiagModulatorDto | null) {
  if (!mod) {
    return "/";
  }

  return (
    mod?.links[0]?.appliances[0]?.localisation ??
    mod?.links[0]?.interfaceName ??
    mod?.location ??
    mod.serialNumber ??
    "/"
  );
}

export function joinStrings(separator: string, ...strings: Array<string | null | undefined>) {
  return strings.filter(Boolean).join(separator);
}

export function resolvePath<T, K extends Flatten<T>>(
  object: T,
  path: Flatten<T>,
  defaultValue?: FlattenType<T, K>,
): FlattenType<T, K> {
  return path.split(".").reduce((o: any, p: any) => (o ? (o as any)[p] : defaultValue), object) as FlattenType<T, K>;
}

export function assert(expr: unknown, msg?: string): asserts expr {
  if (!expr) {
    throw new Error(msg);
  }
}

export function getFlagEmojiByCountry(country?: ECountry | null) {
  if (!country) {
    return "";
  }

  const countryDefinition = findCountryDefinitionByCountry(country);
  if (!countryDefinition) {
    return "";
  }

  return getFlagEmojiByCountryCode(countryDefinition.countryCode);
}

export function getFlagEmojiByCountryCode(countryCode?: ECountryCode | null) {
  if (!countryCode) {
    return "";
  }

  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
}
